import { FormBuilderOptions, ValidationResult } from "./form-builder.model";
import { FormInput } from "./form-input";
import "./form-builder.scss";
import { useMemo } from "react";
import { Box, Paper } from "@mui/material";
import { LayoutService } from "./layout/layout-service";
import { DefaultFormLayout } from "./layout/default-form-layout";
import { prettyPrintName } from "./label-utils";
import { isEmpty } from "lodash";
import { validateFormItem } from "./helper";

export type FormBuilderProps = {
  formBuilderOpts: FormBuilderOptions;
  inputErrors: Record<string, ValidationResult[] | undefined>;
  setInputErrors: React.Dispatch<
    React.SetStateAction<Record<string, ValidationResult[] | undefined>>
  >;
  values: Record<string, unknown>;
  setValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
};

export const FormBuilder: React.FC<FormBuilderProps> = ({
  formBuilderOpts,
  inputErrors,
  setInputErrors,
  values,
  setValues,
}) => {
  /**
   * Preprocess the form builder options to apply any automatic goodness
   *
   * Currently just returns a copy of formBuilderOpts
   */
  const processedFormBuilderOpts: FormBuilderOptions = useMemo(() => {
    const originalFormBuilderOpts = formBuilderOpts;
    return {
      ...originalFormBuilderOpts,
      formItems: originalFormBuilderOpts.formItems.map((fi) => ({
        ...fi,
        label: fi.label ?? prettyPrintName(fi.name),
      })),
    };
  }, [formBuilderOpts]);

  const layoutForm = (formBuilderOpts: FormBuilderOptions) => {
    const map: Record<string, JSX.Element> = formBuilderOpts.formItems.reduce(
      (labelToFormInputMap: Record<string, JSX.Element>, fi) => {
        // Conditional visibility: honor `hidden` (previously declared but ignored)
        // and the `showWhen` predicate, re-evaluated against current values on every
        // change (the whole form re-renders via setValues below).
        if (fi.hidden === true) return labelToFormInputMap;
        if (fi.showWhen && !fi.showWhen(values)) return labelToFormInputMap;
        labelToFormInputMap[fi.label ?? fi.name] = (
          <FormInput
            key={fi.name}
            formLayout={
              formBuilderOpts.formLayoutPlugin ?? DefaultFormLayout.name
            }
            formItem={fi}
            formData={values}
            formErrors={inputErrors}
            setValue={(newValue) => {
              setValues((prevState) => {
                const newValues = {
                  ...prevState,
                  [fi.name]: newValue,
                };
                const errors = validateFormItem(fi, newValues, formBuilderOpts);
                setInputErrors((prevInputErrorState) => {
                  return {
                    ...prevInputErrorState,
                    [fi.name]: isEmpty(errors) ? undefined : errors,
                  };
                });

                return newValues;
              });
            }}
          />
        );
        return labelToFormInputMap;
      },
      {}
    );
    return LayoutService.get()
      .get(formBuilderOpts.formLayoutPlugin ?? DefaultFormLayout.name)
      ?.layoutForm(map);
  };

  return (
    <Paper className="_form-builder.tsx form-builder" elevation={0}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Box>{layoutForm(processedFormBuilderOpts)}</Box>
      </Box>
    </Paper>
  );
};
